/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { useCallback, useEffect, useMemo, useRef } from "react"
import { Button, List, Modal, Select, Spin, Steps } from "@duxton-web/ui"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { TickMarkOutlined, WarningOutlined } from "@duxton-web/icons"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { TroyWebTracker } from "utils/tracking"
import { EventActionEng, EventCategoryEng } from "constants/tracking"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { useFetchMerchantAggregateQuery } from "queries/menuApi"
import { MerchantAggregate } from "types/api"
import useBulkDownload, { TaskStatus } from "./downloader"

interface Props {
  visible: boolean
  close: () => void
}

enum Step {
  // Has to be numeric because it will be used in the Steps component
  SELECTION = 0,
  DOWNLOAD,
  DONE,
}

export default function FeedbackBulkDownload(props: Props) {
  const { visible, close } = props
  const [step, setStep] = React.useState<Step>(Step.SELECTION)
  const { cityList: reduxCityList } = useSelector(
    (state: AppState) => state.menu,
  )
  const hasSentScribeDone = useRef(false)

  const merchantGroupID = useSelector<AppState, string | undefined>(
    state => state.menu?.merchantGroupID,
  )

  const {
    flags: { fetchMerchantAggregate: isFetchMerchantAggregateEnabled },
  } = useRtkQueryMigrationEndpointsFlag()

  const { data: merchantAggregateData } = useFetchMerchantAggregateQuery(
    {
      merchantGroupID,
      fields: [
        MerchantAggregate.cityID,
        MerchantAggregate.businessType,
        MerchantAggregate.deliverOption,
      ],
    },
    { skip: !isFetchMerchantAggregateEnabled || !merchantGroupID },
  )

  const cityList = isFetchMerchantAggregateEnabled
    ? merchantAggregateData?.cities ?? []
    : reduxCityList

  const textMap = useTranslationsMapTyped({
    download: "common.download",
    heading: "feedback.download.heading",
    body: "feedback.download.body",
    cancel: "menu.buttons.cancel",
    errorBody: "feedback.download.error.body",
    allStores: "feedback.filters.allStores",
    select: "menu.menuGroup.create.tab.selectStore",
    progress: "bulkUpload.bulkTask.states.New",
    done: "common.done",
  })

  const [selectedCities, setSelectedCities] = React.useState<string[]>([])

  const cities = useMemo(() => {
    return cityList?.map(({ cityName }) => ({
      value: cityName,
      label: cityName,
    }))
  }, [cityList])

  const onDownload = () => {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.feedbackBulkDownload,
      action: EventActionEng.startBulkDownload,
      labels: {
        merchantGroupID,
        cityCount: cities?.length || 0,
      },
    })
    setStep(Step.DOWNLOAD)
    start()
  }

  // useCallback to avoid downstream keep refreshing
  const onDownloadCallback = useCallback(() => setStep(Step.DONE), [setStep])

  const { cityQueue, start, cancel } = useBulkDownload({
    cities: selectedCities,
    onDone: onDownloadCallback,
  })

  const handleClose = () => {
    setStep(Step.SELECTION)
    setSelectedCities([])
    close()
  }

  useEffect(() => {
    if (step === Step.DONE) {
      // only send once
      if (!hasSentScribeDone.current) {
        hasSentScribeDone.current = true
        TroyWebTracker.sendEvent({
          category: EventCategoryEng.feedbackBulkDownload,
          action: EventActionEng.doneBulkDownload,
          labels: {
            merchantGroupID,
            cityCount: cities?.length || 0,
            doneCount: cityQueue?.filter(x => x.status === TaskStatus.DONE)
              .length,
            errorCount: cityQueue?.filter(x => x.status === TaskStatus.ERROR)
              .length,
          },
        })
      }
    } else {
      // reset
      hasSentScribeDone.current = false
    }
  }, [merchantGroupID, step, cities, cityQueue, hasSentScribeDone])

  return (
    <Modal
      title="Bulk Download"
      open={visible}
      width={600}
      onOk={() => {
        handleClose()
      }}
      destroyOnClose
      closable={false}
      onCancel={() => {
        if (step === Step.DOWNLOAD) {
          TroyWebTracker.sendEvent({
            category: EventCategoryEng.feedbackBulkDownload,
            action: EventActionEng.interruptBulkDownload,
            labels: {
              merchantGroupID,
              cityCount: cities?.length || 0,
              doneCount: cityQueue?.filter(x => x.status === TaskStatus.DONE)
                .length,
              errorCount: cityQueue?.filter(x => x.status === TaskStatus.ERROR)
                .length,
              pendingCount: cityQueue?.filter(
                x => x.status === TaskStatus.PENDING,
              ).length,
              downloadingCount: cityQueue?.filter(
                x => x.status === TaskStatus.DOWNLOADING,
              ).length,
            },
          })
        }
        cancel()
        handleClose()
      }}
      footer={(_, { OkBtn, CancelBtn }) => (
        <>
          {step !== Step.DONE && <CancelBtn />}
          {step === Step.SELECTION && (
            <Button
              type="primary"
              onClick={onDownload}
              data-testid="download"
              disabled={selectedCities.length === 0}
            >
              {textMap.download}
            </Button>
          )}
          {step === Step.DONE && <OkBtn />}
        </>
      )}
    >
      <Steps
        current={step}
        items={[
          {
            title: textMap.select,
          },
          {
            title: textMap.progress,
          },
          {
            title: textMap.done,
          },
        ]}
        style={{ marginBottom: 24 }}
      />
      {step === Step.SELECTION && (
        <Select
          options={cities}
          showSearch
          style={{ width: "100%", marginRight: 16 }}
          size="large"
          mode="multiple"
          value={selectedCities}
          onChange={setSelectedCities}
          dropdownRender={menu => (
            <>
              <Button
                style={{
                  width: "100%",
                  textAlign: "left",
                  borderRadius: 0,
                }}
                ghost
                onClick={() =>
                  setSelectedCities(cities?.map(c => c.value.toString()) || [])
                }
              >
                {textMap.allStores}
              </Button>
              {menu}
            </>
          )}
        />
      )}
      {(step === Step.DOWNLOAD || step === Step.DONE) && (
        <List
          dataSource={cityQueue}
          style={{ maxHeight: 600, overflowY: "scroll" }}
          data-testid="queue"
          renderItem={item => (
            <List.Item
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {item.city}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                {item.status === TaskStatus.DONE && (
                  <TickMarkOutlined style={{ color: "green", fontSize: 20 }} />
                )}
                {item.status === TaskStatus.ERROR && (
                  <>
                    <span style={{ color: "red", marginRight: 12 }}>
                      {textMap.errorBody}
                    </span>
                    <WarningOutlined style={{ color: "red", fontSize: 20 }} />
                  </>
                )}
                {(item.status === TaskStatus.PENDING ||
                  item.status === TaskStatus.DOWNLOADING) && (
                  <Spin size="small" style={{ marginLeft: 8 }} />
                )}
              </div>
            </List.Item>
          )}
        />
      )}
    </Modal>
  )
}
